import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Sparkles, Link2, Clapperboard } from 'lucide-react';
import { PLATFORM_BADGES } from '../../lib/platformLogos';
import { isMarketingMobileLayout, orbitLayoutForViewport, pixelsToRem, remCSSValue } from '../../lib/layoutUnits';
import { DemoStage } from './DemoChrome';
import { VideoAssetCard } from './VideoAssetCard';
import { useDemoAutoplay } from './useDemoAutoplay';
import { DEMO_LIBRARY_VIDEO_THUMB } from './demoStockImages';
import {
  computeOrbitRadiusRem,
  orbitBeamPoint,
  orbitIconTransform,
  orbitRadiusRemToViewBox,
} from './platformOrbit';
import './marketingDemos.css';

const ORBIT_PLATFORMS = PLATFORM_BADGES.slice(0, 6);

export default function PlatformMapDemo() {
  const reduceMotion = useReducedMotion();
  const rootRef = useRef(null);
  const zoneRef = useRef(null);
  const cardRef = useRef(null);
  const [orbitDegrees, setOrbitDegrees] = useState(0);
  const [orbitRadiusRem, setOrbitRadiusRem] = useState(4.75);
  const [orbitRadiusViewBox, setOrbitRadiusViewBox] = useState(42);
  const [orbitZoneSizeRem, setOrbitZoneSizeRem] = useState(18);
  const [mappedAll, setMappedAll] = useState(false);
  const [beamKey, setBeamKey] = useState(0);

  const measureOrbit = useCallback(() => {
    const zone = zoneRef.current;
    const card = cardRef.current;
    if (!zone || !card) return;

    const stackedMobileLayout = isMarketingMobileLayout();
    const layout = orbitLayoutForViewport(stackedMobileLayout);
    const zoneSizeRem = pixelsToRem(zone.offsetWidth);
    const cardSizeRem = pixelsToRem(Math.max(card.offsetWidth, card.offsetHeight));
    const nodeSizeRem = Math.max(
      layout.draftNodeWidthRem,
      layout.draftNodeHeightRem,
      layout.iconNodeRem,
    );
    const radiusRem = computeOrbitRadiusRem(
      zoneSizeRem,
      cardSizeRem,
      nodeSizeRem,
      layout.gapRem,
      layout.fillRatio,
    );
    const zoneSizeRemValue = pixelsToRem(zone.offsetWidth);
    zone.style.setProperty('--orbit-zone-size', remCSSValue(zone.offsetWidth));
    if (stackedMobileLayout) {
      zone.style.setProperty('--orbit-hub-card-width', `${layout.hubCardWidthRem}rem`);
    } else {
      zone.style.removeProperty('--orbit-hub-card-width');
    }
    setOrbitZoneSizeRem(zoneSizeRemValue);
    setOrbitRadiusRem(radiusRem);
    setOrbitRadiusViewBox(orbitRadiusRemToViewBox(radiusRem, zoneSizeRem));
  }, []);

  useLayoutEffect(() => {
    measureOrbit();
    const zone = zoneRef.current;
    if (!zone || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measureOrbit);
    observer.observe(zone);
    if (cardRef.current) observer.observe(cardRef.current);

    const mobileMedia = window.matchMedia('(max-width: 48rem)');
    const onMobileLayoutChange = () => measureOrbit();
    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener('change', onMobileLayoutChange);
    } else {
      mobileMedia.addListener(onMobileLayoutChange);
    }

    return () => {
      observer.disconnect();
      if (mobileMedia.removeEventListener) {
        mobileMedia.removeEventListener('change', onMobileLayoutChange);
      } else {
        mobileMedia.removeListener(onMobileLayoutChange);
      }
    };
  }, [measureOrbit]);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const interval = window.setInterval(() => {
      setOrbitDegrees((d) => (d + 0.42) % 360);
    }, 32);
    return () => window.clearInterval(interval);
  }, [reduceMotion]);

  const onAutoplayTick = useCallback(() => {
    setMappedAll((wasMapped) => {
      if (!wasMapped) {
        setBeamKey((k) => k + 1);
        return true;
      }
      setBeamKey(0);
      return false;
    });
  }, []);

  useDemoAutoplay(rootRef, onAutoplayTick, mappedAll ? 2200 : 1450);

  return (
    <div ref={rootRef}>
      <DemoStage
        className="mkt-demo-stage--orbit-visible"
        visualClassName="mkt-demo-visual--platforms"
      >
        <div className="mkt-platform-map">
          <div className="mkt-platform-map-orbit-shell">
            <div ref={zoneRef} className="mkt-platform-map-orbit-zone">
              <div className="mkt-platform-map-orbit-center">
                {beamKey > 0 && mappedAll ? (
                  <motion.div
                    key={beamKey}
                    className="mkt-platform-map-beams-layer"
                    animate={{ rotate: orbitDegrees }}
                    transition={{ duration: 0, ease: 'linear' }}
                    aria-hidden
                  >
                    <svg
                      className="mkt-platform-map-beams mkt-platform-map-beams--centered"
                      style={{
                        width: `${orbitZoneSizeRem}rem`,
                        height: `${orbitZoneSizeRem}rem`,
                      }}
                      viewBox="0 0 100 100"
                    >
                      <circle
                        className="mkt-platform-map-hub-star"
                        cx="50"
                        cy="50"
                        r="1.35"
                      />
                      {ORBIT_PLATFORMS.map((_, index) => {
                        const pos = orbitBeamPoint(
                          index,
                          ORBIT_PLATFORMS.length,
                          orbitRadiusViewBox,
                        );
                        const BeamLine = reduceMotion ? 'line' : motion.line;
                        const beamProps = reduceMotion
                          ? { x1: 50, y1: 50, x2: pos.x, y2: pos.y, opacity: 0.72 }
                          : {
                              x1: 50,
                              y1: 50,
                              x2: pos.x,
                              y2: pos.y,
                              initial: { opacity: 0 },
                              animate: { opacity: 0.72 },
                              transition: {
                                duration: 0.28,
                                delay: index * 0.04,
                                ease: 'easeOut',
                              },
                            };
                        return (
                          <BeamLine
                            key={`beam-${index}`}
                            {...beamProps}
                            className="mkt-platform-map-beam"
                          />
                        );
                      })}
                    </svg>
                  </motion.div>
                ) : null}

                <motion.div
                  className="mkt-platform-map-orbit-rotator"
                  animate={{ rotate: orbitDegrees }}
                  transition={{ duration: 0, ease: 'linear' }}
                  aria-hidden
                >
                  {ORBIT_PLATFORMS.map((platform, index) => (
                    <div
                      key={platform.name}
                      className={`mkt-platform-map-node mkt-platform-map-node--auto${mappedAll ? ' mkt-platform-map-node--selected mkt-platform-map-node--draft' : ''}`}
                      style={{
                        transform: orbitIconTransform(
                          index,
                          ORBIT_PLATFORMS.length,
                          orbitRadiusRem,
                          orbitDegrees,
                        ),
                      }}
                    >
                      {mappedAll ? (
                        <div className="mkt-platform-map-node-draft-card">
                          <div
                            className="mkt-platform-map-node-draft-thumb"
                            style={{ backgroundImage: `url(${DEMO_LIBRARY_VIDEO_THUMB})` }}
                            aria-hidden
                          />
                          <div className="mkt-platform-map-node-draft-meta">
                            <span
                              className="mkt-platform-map-node-platform-mark"
                              style={{ background: platform.brandColor }}
                            >
                              {platform.logoPath ? (
                                <img
                                  src={platform.logoPath}
                                  alt=""
                                  className="mkt-platform-map-node-logo"
                                />
                              ) : (
                                <span className="mkt-platform-map-node-emoji">
                                  {platform.fallbackIcon}
                                </span>
                              )}
                            </span>
                            <div className="mkt-platform-map-node-draft-copy">
                              <span className="mkt-platform-map-node-draft-name">
                                {platform.name}
                              </span>
                              <span className="mkt-platform-map-node-draft-label">
                                <Clapperboard className="ui-icon-2xs" aria-hidden />
                                Draft
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="mkt-platform-map-node-platform-only"
                          style={{ background: platform.brandColor }}
                        >
                          {platform.logoPath ? (
                            <img
                              src={platform.logoPath}
                              alt=""
                              className="mkt-platform-map-node-logo"
                            />
                          ) : (
                            <span className="mkt-platform-map-node-emoji">
                              {platform.fallbackIcon}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </motion.div>

                <div ref={cardRef} className="mkt-platform-map-card-hub">
                  <VideoAssetCard
                    title="Summer campaign"
                    duration="2:14"
                    thumbnailUrl={DEMO_LIBRARY_VIDEO_THUMB}
                    thumbnailLoading="eager"
                    showLibraryBadge
                    compact
                    className="mkt-platform-map-video-card"
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            className={`mkt-platform-map-all-btn mkt-platform-map-all-btn--auto${mappedAll ? ' mkt-platform-map-all-btn--done' : ''}`}
            disabled
          >
            <Sparkles className="ui-icon-md" aria-hidden />
            {mappedAll ? 'Mapped to all platforms' : 'Map metadata to all platforms'}
          </button>
          <p className="mkt-platform-map-center-label">
            <Link2 className="ui-icon-sm" aria-hidden />
            One action: same title, description &amp; thumbnail on every draft
          </p>
        </div>
      </DemoStage>
    </div>
  );
}
